<script setup>
// Consumer shell — end-user surface. Wraps the local ConsumerLayout (light
// sidebar, blue accent) and houses pages that are user-owned rather than
// platform-admin-owned: Profile, Settings, the eventual Voice Practice,
// Decks, Stats, etc.
//
// Sidebar menu for the consumer surface is HAND-CURATED here (not lifted
// from saas-core's adminMenu). It reflects what an i-Recall learner
// actually does. The pattern, however — single menu config object per shell,
// passed to the layout via :menu — is identical to AdminShell.
import { computed, onMounted } from "vue";
import { useRouter } from "vue-router";
import ConsumerLayout from "@digitalstreams/saas-core/vue/layouts/ConsumerLayout.vue";
import ToastContainer from "@digitalstreams/saas-core/vue/components/ToastContainer.vue";
import { useAuthStore } from "@digitalstreams/saas-core/vue/stores/authStore";

const auth = useAuthStore();
const router = useRouter();

// Admin-class role keys (mirrors saas-core's TENANT_ADMIN_ROLES). A user
// holding any of these on any membership is a tenant admin and gets the
// Team page. Every i-Recall user is tenant_admin of their own personal
// workspace, so in practice this shows for everyone with a workspace.
const TENANT_ADMIN_ROLES = ["super_user", "tenant_admin", "admin", "super_admin"];

onMounted(() => {
  // Memberships power the Team-item visibility; fetch once if absent. Skip for
  // anonymous visitors (the public Topics demo) — there's no tenant to fetch.
  if (auth.isLoggedIn && !auth.tenants?.length) auth.fetchTenants?.();
});

const isTenantAdmin = computed(() =>
  (auth.tenants || []).some((t) =>
    (t.roles || []).some((r) => TENANT_ADMIN_ROLES.includes(r))
  )
);

// i-Recall's consumer-surface menu. Each item points at a route that
// resolves to a ConsumerShell child route in router/index.js. Computed so
// the Team item appears once memberships load (and the Platform section
// only for platform admins).
const menu = computed(() => {
  // Anonymous (public demo): browse/play Topics, with a clear path to sign up.
  if (!auth.isLoggedIn) {
    return [
      {
        // No section heading on the primary nav — items sit directly under the logo.
        label: "",
        items: [
          { label: "Topics", to: "/topics", icon: "bi bi-mic-fill" }
        ]
      },
      {
        label: "Account",
        items: [
          { label: "Sign in",             to: "/login",    icon: "bi bi-box-arrow-in-right" },
          { label: "Create free account", to: "/register", icon: "bi bi-person-plus" }
        ]
      }
    ];
  }

  const m = [
    {
      // No section heading on the primary nav — items sit directly under the logo.
      label: "",
      items: [
        { label: "Home",        to: "/home",           icon: "bi bi-house-door" },
        { label: "Topics",      to: "/topics",          icon: "bi bi-mic-fill" },
        { label: "My Library",  to: "/library",          icon: "bi bi-collection" },
        { label: "History",     to: "/topics/history", icon: "bi bi-clock-history" }
      ]
    },
    {
      label: "Account",
      items: [
        { label: "My Profile",  to: "/profile",         icon: "bi bi-person-circle" },
        { label: "Settings",    to: "/settings",        icon: "bi bi-gear" },
        // Tenant-admin surface — manage your organization's members + projects.
        ...(isTenantAdmin.value
          ? [
              { label: "Team",     to: "/team",     icon: "bi bi-people" },
              { label: "Projects", to: "/projects", icon: "bi bi-folder" }
            ]
          : [])
      ]
    }
  ];

  // Platform super-admin ONLY: a discoverable entry to curate the public/homepage
  // catalog (bulk-seed + auto-publish). Distinct from the /ds-ops console (still
  // deliberately unadvertised) — this is the day-to-day "keep the site fresh"
  // tool, so it IS surfaced, but only to isAdmin, in its own "Platform" section
  // so it reads as clearly special/separate from personal content.
  if (auth.isAdmin) {
    m.push({
      label: "Platform (admin)",
      items: [
        { label: "Curate content", to: "/curate", icon: "bi bi-stars" }
      ]
    });
  }

  // NOTE: deliberately NO "Admin console" link here. The platform-admin
  // console is a separate, specialised surface reached intentionally by its
  // URL — never advertised from the consumer sidebar, not even to admins.
  // Keeps the admin surface off every user's radar (per Peter, 2026-06-13).
  return m;
});

async function onLogout() {
  await auth.logout();
  router.push("/topics"); // land on the public demo, not a login wall
}
</script>

<template>
  <ConsumerLayout
    app-name="i-Recall"
    home-to="/home"
    :user="auth.user"
    :menu="menu"
    @logout="onLogout"
  >
    <!-- Brand: the full i-Recall wordmark logo (no text — the logo already reads
         "i-Recall"). When the sidebar collapses to a rail, the wordmark can't
         fit, so fall back to the square iR monogram. -->
    <template #brand="{ collapsed }">
      <router-link to="/home" class="ds-shell-brand-link">
        <img v-if="collapsed" :src="'/images/icon.png'" alt="i-Recall" class="ir-brand-icon" width="24" height="24" />
        <img v-else :src="'/images/i-recall-logo.png'" alt="i-Recall" class="ir-brand-logo" width="800" height="463" />
      </router-link>
    </template>

    <router-view />
  </ConsumerLayout>
  <!-- Renders the useToast() queue — saas-core screens (UserProfile, etc.)
       call useToast(); without this host their toasts are silently dropped. -->
  <ToastContainer />
</template>

<style scoped>
.ir-brand-icon {
  display: block;
  height: 24px;
  width: 24px;
  object-fit: contain;
}
/* The default brand bar is a fixed 56px tall and vertically-centres its child,
   so a taller wordmark overflows - clipped at the top, spilling onto the "LEARN"
   nav label below. Let the bar grow to the logo and give it space above. */
:deep(.ds-shell-brand) {
  height: auto;
  align-items: flex-start;
  padding: 6px 1rem 12px;
}
.ir-brand-logo {
  display: block;
  width: 100%;
  max-width: 140px;
  height: auto;
}
</style>
